/**
 * Capture -> new-Wheel seeding (deck screen `06-create-new-confirm`).
 *
 * WHY THIS FILE EXISTS
 * The Capture inbox's "Create Wheel" action used to dispatch the create-a-Wheel
 * wizard with NO payload, so the selected capture(s) were silently dropped: the
 * wheel was created with no reference to them and every selected capture stayed
 * `attached_wheel: null`. These pure helpers carry the selected capture ids
 * through the wizard and attach them to the newly created wheel using the
 * EXISTING capture->wheel attach mechanism (`POST /capture/{id}/attach/` with a
 * `wheel_id`) — the same call the "Add to Wheel" picker already makes. No second
 * attach path is invented.
 *
 * Everything here is pure/injectable so the 0 / 1 / many, cancelled-midway and
 * create-failed cases are unit-testable without a browser.
 */

/**
 * Normalize whatever the Capture page hands us into a clean id list.
 *
 * Accepts: a single capture object, a single id, an array of capture objects,
 * an array of ids, `null`/`undefined` (the row-menu entry point passes one item;
 * the bulk bar passes the selected array; the plain toolbar passes nothing).
 * De-duplicates, drops null/undefined ids, and always returns a NEW array so it
 * is safe to store in redux state.
 */
export const captureIdsFrom = (input) => {
  const list = Array.isArray(input) ? input : input == null ? [] : [input];
  const out = [];
  list.forEach((entry) => {
    if (entry == null) return;
    const id = typeof entry === "object" ? entry.id : entry;
    if (id == null || id === "") return;
    if (!out.includes(id)) out.push(id);
  });
  return out;
};

/**
 * Attach every seeded capture to the freshly created wheel.
 *
 * `attach` is injected (in the app it is the `useAttachCaptureItem` mutation's
 * `mutateAsync`) so this stays testable and so there is exactly ONE attach
 * implementation in the codebase.
 *
 * Contract — deliberately total, never throws:
 *   * no ids, or no wheelId -> zero requests fire. This is the cancelled-midway
 *     and create-failed case: a wheel that was never created (or a wizard the
 *     user closed) must leave no orphan attach behind.
 *   * a per-item failure does NOT abort the rest — a partial success is reported
 *     honestly instead of being hidden behind an all-or-nothing throw. Captures
 *     that failed stay exactly as they were (still in the inbox, unattached), so
 *     the user can retry from "Add to Wheel".
 *
 * @returns {Promise<{attached: Array, failed: Array, total: number}>}
 */
export const attachSeedCaptures = async ({ ids, wheelId, attach } = {}) => {
  const list = captureIdsFrom(ids);
  const empty = { attached: [], failed: [], total: list.length };
  if (list.length === 0) return empty;
  if (wheelId == null || wheelId === "") return { ...empty, failed: [] };
  if (typeof attach !== "function") return empty;

  const results = await Promise.all(
    list.map((id) =>
      Promise.resolve()
        .then(() => attach(id))
        .then(() => ({ id, ok: true }))
        .catch(() => ({ id, ok: false }))
    )
  );

  return {
    attached: results.filter((r) => r.ok).map((r) => r.id),
    failed: results.filter((r) => !r.ok).map((r) => r.id),
    total: list.length,
  };
};

/**
 * User-facing copy for the seeding outcome. `null` means "say nothing" —
 * nothing was seeded, so the wizard's own "Wheel created successfully" toast is
 * the whole story. On success this is the SINGLE toast (create + attach
 * combined) so Capture create does not stack two success toasts.
 */
export const seedResultMessage = ({ attached = [], failed = [], total = 0 } = {}) => {
  if (total === 0) return null;
  if (failed.length === 0) {
    return attached.length === 1
      ? {
          type: "success",
          text: "Wheel created and capture added successfully",
        }
      : {
          type: "success",
          text: "Wheel created and captures added successfully",
        };
  }
  if (attached.length === 0) {
    return {
      type: "error",
      text:
        failed.length === 1
          ? "Wheel created, but the capture couldn't be added. It's still in your inbox."
          : `Wheel created, but the ${failed.length} captures couldn't be added. They're still in your inbox.`,
    };
  }
  return {
    type: "error",
    text: `Wheel created. ${attached.length} of ${total} captures added — the rest are still in your inbox.`,
  };
};
